/**
 * Per-struggle Bible verse curation — single source of truth for the verse
 * lists Illuminate surfaces against the 8 struggle types from step 5.
 *
 * Used by:
 *   - Step 6 (hunger reflection — synthesis): card carousel + sequential
 *     verse cycling ("Verse N of M"). User cycles through ALL verses in
 *     order via the shuffle / card click.
 *   - Future: chat AI demo (step 8) seed verses, Bible Reader recommended
 *     verses, post-onboarding study path suggestions.
 *
 * Source rationale:
 *   References were curated from public-domain topical indexes (Nave's Topical
 *   Bible, Treasury of Scripture Knowledge) and cross-checked against widely
 *   recognized topical Bible groupings. Each list aims for 15-35 of the most
 *   well-known, theologically substantive verses on the topic — not exhaustive,
 *   but representative.
 *
 * Data shape (Mason's lock 2026-05-13):
 *   Each struggle has a single `verses[]` array with full text + citation.
 *   `verses.length` is the honest pool size — what the indicator counts to.
 *
 * Translation note:
 *   Text is rendered in NKJV-compatible English (no copyright issue for the
 *   short quotations under fair use). Full-text rendering elsewhere (Bible
 *   Reader) should fetch from API.Bible (licensed) or bible-api.com (PD).
 */

export const STRUGGLE_VERSES = {
  prayer_life: {
    label: 'Prayer life',
    eyebrow: 'For prayer life',
    verses: [
      {
        text: 'The Lord is near to all who call upon Him, to all who call upon Him in truth.',
        citation: 'Psalm 145:18',
      },
      {
        text: 'Be anxious for nothing, but in everything by prayer and supplication, with thanksgiving, let your requests be made known to God.',
        citation: 'Philippians 4:6-7',
      },
      { text: 'Pray without ceasing.', citation: '1 Thessalonians 5:17' },
      {
        text: 'When you pray, go into your room, and when you have shut your door, pray to your Father who is in the secret place.',
        citation: 'Matthew 6:6',
      },
      {
        text: 'The effective, fervent prayer of a righteous man avails much.',
        citation: 'James 5:16',
      },
      {
        text: 'Ask, and it will be given to you; seek, and you will find; knock, and it will be opened to you.',
        citation: 'Matthew 7:7-8',
      },
      {
        text: 'Whatever things you ask when you pray, believe that you receive them, and you will have them.',
        citation: 'Mark 11:24',
      },
      {
        text: 'The Spirit also helps in our weaknesses. For we do not know what we should pray for as we ought, but the Spirit Himself makes intercession for us.',
        citation: 'Romans 8:26',
      },
      {
        text: 'If we ask anything according to His will, He hears us.',
        citation: '1 John 5:14-15',
      },
      {
        text: 'Let us therefore come boldly to the throne of grace, that we may obtain mercy and find grace to help in time of need.',
        citation: 'Hebrews 4:16',
      },
      {
        text: 'Call to Me, and I will answer you, and show you great and mighty things, which you do not know.',
        citation: 'Jeremiah 33:3',
      },
      {
        text: 'My voice You shall hear in the morning, O Lord; in the morning I will direct it to You, and I will look up.',
        citation: 'Psalm 5:3',
      },
      { text: 'Men always ought to pray and not lose heart.', citation: 'Luke 18:1' },
      {
        text: 'Our Father in heaven, hallowed be Your name. Your kingdom come. Your will be done on earth as it is in heaven.',
        citation: 'Matthew 6:9-13',
      },
      {
        text: 'The righteous cry out, and the Lord hears, and delivers them out of all their troubles.',
        citation: 'Psalm 34:17',
      },
      {
        text: 'If I regard iniquity in my heart, the Lord will not hear; but certainly God has heard me; He has attended to the voice of my prayer.',
        citation: 'Psalm 66:18-20',
      },
      {
        text: 'It shall come to pass that before they call, I will answer; and while they are still speaking, I will hear.',
        citation: 'Isaiah 65:24',
      },
      {
        text: 'Continue earnestly in prayer, being vigilant in it with thanksgiving.',
        citation: 'Colossians 4:2',
      },
      {
        text: 'The eyes of the Lord are on the righteous, and His ears are open to their prayers.',
        citation: '1 Peter 3:12',
      },
      {
        text: 'Watch and pray, lest you enter into temptation. The spirit indeed is willing, but the flesh is weak.',
        citation: 'Matthew 26:41',
      },
      {
        text: 'Praying always with all prayer and supplication in the Spirit, being watchful to this end with all perseverance.',
        citation: 'Ephesians 6:18',
      },
      {
        text: 'Whatever you ask in My name, that I will do, that the Father may be glorified in the Son.',
        citation: 'John 14:13-14',
      },
      {
        text: 'Give ear, O Lord, to my prayer; and attend to the voice of my supplications. In the day of my trouble I will call upon You, for You will answer me.',
        citation: 'Psalm 86:6-7',
      },
      {
        text: 'He shall call upon Me, and I will answer him; I will be with him in trouble; I will deliver him and honor him.',
        citation: 'Psalm 91:15',
      },
    ],
  },

  doubt: {
    label: 'Doubt',
    eyebrow: 'For doubt',
    verses: [
      { text: 'Lord, I believe; help my unbelief!', citation: 'Mark 9:24' },
      {
        text: 'If any of you lacks wisdom, let him ask of God, who gives to all liberally and without reproach, and it will be given to him. But let him ask in faith, with no doubting.',
        citation: 'James 1:5-8',
      },
      { text: 'O you of little faith, why did you doubt?', citation: 'Matthew 14:31' },
      {
        text: 'Do not be unbelieving, but believing. Blessed are those who have not seen and yet have believed.',
        citation: 'John 20:27-29',
      },
      {
        text: 'Now faith is the substance of things hoped for, the evidence of things not seen.',
        citation: 'Hebrews 11:1',
      },
      {
        text: 'Without faith it is impossible to please Him, for he who comes to God must believe that He is, and that He is a rewarder of those who diligently seek Him.',
        citation: 'Hebrews 11:6',
      },
      {
        text: 'If you have faith as a mustard seed, you will say to this mountain, “Move from here to there,” and it will move; and nothing will be impossible for you.',
        citation: 'Matthew 17:20',
      },
      { text: 'Whatever is not from faith is sin.', citation: 'Romans 14:23' },
      { text: 'On some have compassion, making a distinction.', citation: 'Jude 1:22' },
      {
        text: 'Whoever does not doubt in his heart, but believes that those things he says will be done, he will have whatever he says.',
        citation: 'Mark 11:23',
      },
      {
        text: 'If you have faith and do not doubt, you will not only do what was done to the fig tree, but also if you say to this mountain, “Be removed,” it will be done.',
        citation: 'Matthew 21:21',
      },
      {
        text: 'For now we see in a mirror, dimly, but then face to face. Now I know in part, but then I shall know just as I also am known.',
        citation: '1 Corinthians 13:12',
      },
      {
        text: 'My God, My God, why have You forsaken Me? Why are You so far from helping Me, and from the words of My groaning?',
        citation: 'Psalm 22:1-2',
      },
      {
        text: 'O Lord, how long shall I cry, and You will not hear? Even cry out to You, “Violence!” and You will not save.',
        citation: 'Habakkuk 1:2-3',
      },
      {
        text: 'When I thought how to understand this, it was too painful for me — until I went into the sanctuary of God; then I understood their end.',
        citation: 'Psalm 73:16-17',
      },
      {
        text: 'This I recall to my mind, therefore I have hope. Through the Lord’s mercies we are not consumed.',
        citation: 'Lamentations 3:19-26',
      },
      {
        text: 'I know that my Redeemer lives, and He shall stand at last on the earth; and after my skin is destroyed, this I know, that in my flesh I shall see God.',
        citation: 'Job 19:25-27',
      },
      {
        text: 'I know whom I have believed and am persuaded that He is able to keep what I have committed to Him until that Day.',
        citation: '2 Timothy 1:12',
      },
    ],
  },

  scripture_comprehension: {
    label: 'Understanding Scripture',
    eyebrow: 'For understanding Scripture',
    verses: [
      {
        text: 'Open my eyes, that I may see wondrous things from Your law.',
        citation: 'Psalm 119:18',
      },
      { text: 'Your word is a lamp to my feet and a light to my path.', citation: 'Psalm 119:105' },
      {
        text: 'All Scripture is given by inspiration of God, and is profitable for doctrine, for reproof, for correction, for instruction in righteousness.',
        citation: '2 Timothy 3:16-17',
      },
      {
        text: 'For the word of God is living and powerful, and sharper than any two-edged sword.',
        citation: 'Hebrews 4:12',
      },
      {
        text: 'Be doers of the word, and not hearers only, deceiving yourselves.',
        citation: 'James 1:22-25',
      },
      {
        text: 'They received the word with all readiness, and searched the Scriptures daily to find out whether these things were so.',
        citation: 'Acts 17:11',
      },
      {
        text: 'He opened their understanding, that they might comprehend the Scriptures.',
        citation: 'Luke 24:45',
      },
      {
        text: 'The Helper, the Holy Spirit, whom the Father will send in My name, He will teach you all things, and bring to your remembrance all things that I said to you.',
        citation: 'John 14:26',
      },
      {
        text: 'The natural man does not receive the things of the Spirit of God, for they are foolishness to him; nor can he know them, because they are spiritually discerned.',
        citation: '1 Corinthians 2:14',
      },
      {
        text: 'The entrance of Your words gives light; it gives understanding to the simple.',
        citation: 'Psalm 119:130',
      },
      {
        text: 'If you receive my words, and treasure my commands within you, you will understand the fear of the Lord, and find the knowledge of God.',
        citation: 'Proverbs 2:1-6',
      },
      {
        text: 'So shall My word be that goes forth from My mouth; it shall not return to Me void, but it shall accomplish what I please.',
        citation: 'Isaiah 55:10-11',
      },
      {
        text: 'This Book of the Law shall not depart from your mouth, but you shall meditate in it day and night, that you may observe to do according to all that is written in it.',
        citation: 'Joshua 1:8',
      },
      {
        text: 'His delight is in the law of the Lord, and in His law he meditates day and night.',
        citation: 'Psalm 1:1-3',
      },
      {
        text: 'Man shall not live by bread alone, but by every word that proceeds from the mouth of God.',
        citation: 'Matthew 4:4',
      },
      {
        text: 'The grass withers, and its flower falls away, but the word of the Lord endures forever.',
        citation: '1 Peter 1:24-25',
      },
      {
        text: 'No prophecy of Scripture is of any private interpretation, for prophecy never came by the will of man, but holy men of God spoke as they were moved by the Holy Spirit.',
        citation: '2 Peter 1:20-21',
      },
      {
        text: 'These words which I command you today shall be in your heart. You shall teach them diligently to your children.',
        citation: 'Deuteronomy 6:6-9',
      },
      {
        text: 'Your word I have hidden in my heart, that I might not sin against You.',
        citation: 'Psalm 119:11',
      },
      {
        text: 'Whatever things were written before were written for our learning, that we through the patience and comfort of the Scriptures might have hope.',
        citation: 'Romans 15:4',
      },
      {
        text: 'You welcomed it not as the word of men, but as it is in truth, the word of God, which also effectively works in you who believe.',
        citation: '1 Thessalonians 2:13',
      },
      {
        text: 'Let the word of Christ dwell in you richly in all wisdom, teaching and admonishing one another.',
        citation: 'Colossians 3:16',
      },
      {
        text: 'Your words were found, and I ate them, and Your word was to me the joy and rejoicing of my heart.',
        citation: 'Jeremiah 15:16',
      },
      {
        text: 'Ezra had prepared his heart to seek the Law of the Lord, and to do it, and to teach statutes and ordinances in Israel.',
        citation: 'Ezra 7:10',
      },
      {
        text: 'So they read distinctly from the book, in the Law of God; and they gave the sense, and helped them to understand the reading.',
        citation: 'Nehemiah 8:8',
      },
      {
        text: 'The law of the Lord is perfect, converting the soul; the testimony of the Lord is sure, making wise the simple.',
        citation: 'Psalm 19:7-11',
      },
      {
        text: 'Be diligent to present yourself approved to God, a worker who does not need to be ashamed, rightly dividing the word of truth.',
        citation: '2 Timothy 2:15',
      },
      {
        text: 'Till I come, give attention to reading, to exhortation, to doctrine.',
        citation: '1 Timothy 4:13',
      },
      {
        text: 'The grass withers, the flower fades, but the word of our God stands forever.',
        citation: 'Isaiah 40:8',
      },
      {
        text: 'The entirety of Your word is truth, and every one of Your righteous judgments endures forever.',
        citation: 'Psalm 119:160',
      },
    ],
  },

  defending_faith: {
    label: 'Defending my faith',
    eyebrow: 'For defending the faith',
    verses: [
      {
        text: 'Always be ready to give a defense to everyone who asks you a reason for the hope that is in you, with meekness and fear.',
        citation: '1 Peter 3:15',
      },
      {
        text: 'Contend earnestly for the faith which was once for all delivered to the saints.',
        citation: 'Jude 1:3',
      },
      {
        text: 'Paul reasoned with them from the Scriptures, explaining and demonstrating that the Christ had to suffer and rise again from the dead.',
        citation: 'Acts 17:2-3',
      },
      {
        text: 'He vigorously refuted the Jews publicly, showing from the Scriptures that Jesus is the Christ.',
        citation: 'Acts 18:28',
      },
      {
        text: 'Holding fast the faithful word as he has been taught, that he may be able, by sound doctrine, both to exhort and convict those who contradict.',
        citation: 'Titus 1:9',
      },
      {
        text: 'The weapons of our warfare are not carnal but mighty in God for pulling down strongholds, casting down arguments and every high thing that exalts itself against the knowledge of God.',
        citation: '2 Corinthians 10:4-5',
      },
      {
        text: 'In humility correcting those who are in opposition, if God perhaps will grant them repentance, so that they may know the truth.',
        citation: '2 Timothy 2:25',
      },
      {
        text: 'In the defense and confirmation of the gospel, you all are partakers with me of grace.',
        citation: 'Philippians 1:7',
      },
      {
        text: 'Knowing that I am appointed for the defense of the gospel.',
        citation: 'Philippians 1:17',
      },
      {
        text: 'Let your speech always be with grace, seasoned with salt, that you may know how you ought to answer each one.',
        citation: 'Colossians 4:6',
      },
      { text: 'Brethren and fathers, hear my defense before you now.', citation: 'Acts 22:1' },
      {
        text: 'I am not ashamed of the gospel of Christ, for it is the power of God to salvation for everyone who believes.',
        citation: 'Romans 1:16',
      },
      {
        text: 'Preach the word! Be ready in season and out of season. Convince, rebuke, exhort, with all longsuffering and teaching.',
        citation: '2 Timothy 4:2',
      },
      {
        text: 'Do not worry about how or what you should speak. For it will be given to you in that hour what you should speak; for it is not you who speak, but the Spirit of your Father who speaks in you.',
        citation: 'Matthew 10:18-20',
      },
      {
        text: 'I will give you a mouth and wisdom which all your adversaries will not be able to contradict or resist.',
        citation: 'Luke 21:14-15',
      },
      {
        text: 'Having a good conscience, that when they defame you as evildoers, those who revile your good conduct in Christ may be ashamed.',
        citation: '1 Peter 3:16',
      },
      {
        text: 'I would to God that not only you, but also all who hear me today, might become both almost and altogether such as I am, except for these chains.',
        citation: 'Acts 26:1-29',
      },
      {
        text: 'Paul went into the synagogue and spoke boldly for three months, reasoning and persuading concerning the things of the kingdom of God.',
        citation: 'Acts 19:8-10',
      },
      {
        text: 'Knowing, therefore, the terror of the Lord, we persuade men.',
        citation: '2 Corinthians 5:11',
      },
      {
        text: 'Take up the whole armor of God... having girded your waist with truth, having put on the breastplate of righteousness, and take the helmet of salvation, and the sword of the Spirit, which is the word of God.',
        citation: 'Ephesians 6:13-17',
      },
    ],
  },

  sermon_prep: {
    label: 'Sermon prep',
    eyebrow: 'For preparing sermons',
    verses: [
      {
        text: 'Preach the word! Be ready in season and out of season. Convince, rebuke, exhort, with all longsuffering and teaching.',
        citation: '2 Timothy 4:2',
      },
      {
        text: 'It pleased God through the foolishness of the message preached to save those who believe. We preach Christ crucified.',
        citation: '1 Corinthians 1:21-23',
      },
      {
        text: 'How shall they hear without a preacher? How beautiful are the feet of those who preach the gospel of peace, who bring glad tidings of good things!',
        citation: 'Romans 10:14-15',
      },
      {
        text: 'Be diligent to present yourself approved to God, a worker who does not need to be ashamed, rightly dividing the word of truth.',
        citation: '2 Timothy 2:15',
      },
      {
        text: 'I have not shunned to declare to you the whole counsel of God.',
        citation: 'Acts 20:27',
      },
      {
        text: 'But as for you, speak the things which are proper for sound doctrine.',
        citation: 'Titus 2:1',
      },
      {
        text: 'Give attention to reading, to exhortation, to doctrine. Take heed to yourself and to the doctrine.',
        citation: '1 Timothy 4:13-16',
      },
      {
        text: 'They read distinctly from the book, in the Law of God; and they gave the sense, and helped them to understand the reading.',
        citation: 'Nehemiah 8:8',
      },
      {
        text: 'Ezra had prepared his heart to seek the Law of the Lord, and to do it, and to teach statutes and ordinances in Israel.',
        citation: 'Ezra 7:10',
      },
      {
        text: 'Go therefore and make disciples of all the nations... teaching them to observe all things that I have commanded you; and lo, I am with you always, even to the end of the age.',
        citation: 'Matthew 28:19-20',
      },
      {
        text: 'If anyone speaks, let him speak as the oracles of God. If anyone ministers, let him do it as with the ability which God supplies.',
        citation: '1 Peter 4:11',
      },
      {
        text: 'Him we preach, warning every man and teaching every man in all wisdom, that we may present every man perfect in Christ Jesus.',
        citation: 'Colossians 1:28',
      },
      {
        text: 'He Himself gave some to be apostles, some prophets, some evangelists, and some pastors and teachers, for the equipping of the saints for the work of ministry.',
        citation: 'Ephesians 4:11-12',
      },
      {
        text: 'Let not many of you become teachers, my brethren, knowing that we shall receive a stricter judgment.',
        citation: 'James 3:1',
      },
      {
        text: 'We do not preach ourselves, but Christ Jesus the Lord, and ourselves your bondservants for Jesus’ sake.',
        citation: '2 Corinthians 4:5',
      },
      {
        text: 'We will give ourselves continually to prayer and to the ministry of the word.',
        citation: 'Acts 6:4',
      },
    ],
  },

  daily_walk: {
    label: 'Daily walk with God',
    eyebrow: 'For your daily walk',
    verses: [
      {
        text: 'He has shown you, O man, what is good; and what does the Lord require of you but to do justly, to love mercy, and to walk humbly with your God?',
        citation: 'Micah 6:8',
      },
      {
        text: 'Walk in the Spirit, and you shall not fulfill the lust of the flesh.',
        citation: 'Galatians 5:16',
      },
      {
        text: 'For you were once darkness, but now you are light in the Lord. Walk as children of light.',
        citation: 'Ephesians 5:8',
      },
      {
        text: 'As you have therefore received Christ Jesus the Lord, so walk in Him.',
        citation: 'Colossians 2:6',
      },
      {
        text: 'If we walk in the light as He is in the light, we have fellowship with one another, and the blood of Jesus Christ His Son cleanses us from all sin.',
        citation: '1 John 1:7',
      },
      {
        text: 'Just as Christ was raised from the dead by the glory of the Father, even so we also should walk in newness of life.',
        citation: 'Romans 6:4',
      },
      {
        text: 'If we live in the Spirit, let us also walk in the Spirit.',
        citation: 'Galatians 5:25',
      },
      { text: 'For we walk by faith, not by sight.', citation: '2 Corinthians 5:7' },
      {
        text: 'Abide in Me, and I in you. As the branch cannot bear fruit of itself, unless it abides in the vine, neither can you, unless you abide in Me.',
        citation: 'John 15:4-5',
      },
      {
        text: 'His delight is in the law of the Lord, and in His law he meditates day and night.',
        citation: 'Psalm 1:1-3',
      },
      {
        text: 'Trust in the Lord with all your heart, and lean not on your own understanding; in all your ways acknowledge Him, and He shall direct your paths.',
        citation: 'Proverbs 3:5-6',
      },
      {
        text: 'The Lord is my shepherd; I shall not want. He makes me to lie down in green pastures; He leads me beside the still waters.',
        citation: 'Psalm 23',
      },
      {
        text: 'But seek first the kingdom of God and His righteousness, and all these things shall be added to you.',
        citation: 'Matthew 6:33',
      },
      {
        text: 'Let us run with endurance the race that is set before us, looking unto Jesus, the author and finisher of our faith.',
        citation: 'Hebrews 12:1-2',
      },
      {
        text: 'I have set the Lord always before me; because He is at my right hand I shall not be moved.',
        citation: 'Psalm 16:8',
      },
      { text: 'Draw near to God and He will draw near to you.', citation: 'James 4:8' },
      {
        text: 'Through the Lord’s mercies we are not consumed, because His compassions fail not. They are new every morning; great is Your faithfulness.',
        citation: 'Lamentations 3:22-23',
      },
      {
        text: 'The steps of a good man are ordered by the Lord, and He delights in his way.',
        citation: 'Psalm 37:23',
      },
      {
        text: 'Let the words of my mouth and the meditation of my heart be acceptable in Your sight, O Lord, my strength and my Redeemer.',
        citation: 'Psalm 19:14',
      },
      {
        text: 'Forgetting those things which are behind and reaching forward to those things which are ahead, I press toward the goal for the prize of the upward call of God in Christ Jesus.',
        citation: 'Philippians 3:13-14',
      },
      {
        text: 'To love the Lord your God, to walk in all His ways, to keep His commandments, to hold fast to Him, and to serve Him with all your heart and with all your soul.',
        citation: 'Joshua 22:5',
      },
      {
        text: 'What does the Lord your God require of you, but to fear the Lord your God, to walk in all His ways, and to love Him, to serve Him with all your heart and with all your soul.',
        citation: 'Deuteronomy 10:12',
      },
      {
        text: 'Those who wait on the Lord shall renew their strength; they shall mount up with wings like eagles, they shall run and not be weary, they shall walk and not faint.',
        citation: 'Isaiah 40:31',
      },
      {
        text: 'Rejoice always, pray without ceasing, in everything give thanks; for this is the will of God in Christ Jesus for you.',
        citation: '1 Thessalonians 5:16-18',
      },
      {
        text: 'Whatever things are true, whatever things are noble, whatever things are just, whatever things are pure, whatever things are lovely... meditate on these things.',
        citation: 'Philippians 4:8',
      },
      {
        text: 'Present your bodies a living sacrifice, holy, acceptable to God, which is your reasonable service. And do not be conformed to this world, but be transformed by the renewing of your mind.',
        citation: 'Romans 12:1-2',
      },
      {
        text: 'Come to Me, all you who labor and are heavy laden, and I will give you rest. Take My yoke upon you and learn from Me, for I am gentle and lowly in heart.',
        citation: 'Matthew 11:28-30',
      },
      {
        text: 'I have been crucified with Christ; it is no longer I who live, but Christ lives in me; and the life which I now live in the flesh I live by faith in the Son of God.',
        citation: 'Galatians 2:20',
      },
    ],
  },

  staying_consistent: {
    label: 'Staying consistent',
    eyebrow: 'For staying consistent',
    verses: [
      {
        text: 'Through the Lord’s mercies we are not consumed, because His compassions fail not. They are new every morning; great is Your faithfulness.',
        citation: 'Lamentations 3:22-23',
      },
      {
        text: 'Let us run with endurance the race that is set before us, looking unto Jesus, the author and finisher of our faith.',
        citation: 'Hebrews 12:1-2',
      },
      {
        text: 'And let us not grow weary while doing good, for in due season we shall reap if we do not lose heart.',
        citation: 'Galatians 6:9',
      },
      {
        text: 'Blessed is the man who endures temptation; for when he has been approved, he will receive the crown of life which the Lord has promised to those who love Him.',
        citation: 'James 1:12',
      },
      {
        text: 'I have fought the good fight, I have finished the race, I have kept the faith.',
        citation: '2 Timothy 4:7',
      },
      {
        text: 'Forgetting those things which are behind and reaching forward to those things which are ahead, I press toward the goal for the prize of the upward call of God in Christ Jesus.',
        citation: 'Philippians 3:13-14',
      },
      {
        text: 'Therefore, my beloved brethren, be steadfast, immovable, always abounding in the work of the Lord, knowing that your labor is not in vain in the Lord.',
        citation: '1 Corinthians 15:58',
      },
      {
        text: 'Let us hold fast the confession of our hope without wavering, for He who promised is faithful.',
        citation: 'Hebrews 10:23',
      },
      {
        text: 'You have need of endurance, so that after you have done the will of God, you may receive the promise.',
        citation: 'Hebrews 10:36',
      },
      {
        text: 'Tribulation produces perseverance; and perseverance, character; and character, hope.',
        citation: 'Romans 5:3-4',
      },
      {
        text: 'Count it all joy when you fall into various trials, knowing that the testing of your faith produces patience. But let patience have its perfect work, that you may be perfect and complete, lacking nothing.',
        citation: 'James 1:2-4',
      },
      {
        text: 'After you have suffered a while, perfect, establish, strengthen, and settle you.',
        citation: '1 Peter 5:10',
      },
      { text: 'He who endures to the end shall be saved.', citation: 'Matthew 24:13' },
      {
        text: 'Be faithful until death, and I will give you the crown of life.',
        citation: 'Revelation 2:10',
      },
      {
        text: 'Though our outward man is perishing, yet the inward man is being renewed day by day. For our light affliction, which is but for a moment, is working for us a far more exceeding and eternal weight of glory.',
        citation: '2 Corinthians 4:16-18',
      },
      {
        text: 'Wait on the Lord; be of good courage, and He shall strengthen your heart; wait, I say, on the Lord!',
        citation: 'Psalm 27:14',
      },
      {
        text: 'Those who wait on the Lord shall renew their strength; they shall mount up with wings like eagles, they shall run and not be weary, they shall walk and not faint.',
        citation: 'Isaiah 40:31',
      },
      {
        text: 'Being confident of this very thing, that He who has begun a good work in you will complete it until the day of Jesus Christ.',
        citation: 'Philippians 1:6',
      },
      {
        text: 'We have become partakers of Christ if we hold the beginning of our confidence steadfast to the end.',
        citation: 'Hebrews 3:14',
      },
      {
        text: 'The steps of a good man are ordered by the Lord, and He delights in his way. Though he fall, he shall not be utterly cast down; for the Lord upholds him with His hand.',
        citation: 'Psalm 37:23-24',
      },
      {
        text: 'Run in such a way that you may obtain it. And everyone who competes for the prize is temperate in all things.',
        citation: '1 Corinthians 9:24-27',
      },
      {
        text: 'If indeed you continue in the faith, grounded and steadfast, and are not moved away from the hope of the gospel.',
        citation: 'Colossians 1:23',
      },
    ],
  },

  other: {
    label: 'Something else',
    eyebrow: 'Wherever you are',
    verses: [
      {
        text: 'Come to Me, all you who labor and are heavy laden, and I will give you rest. Take My yoke upon you and learn from Me, for I am gentle and lowly in heart, and you will find rest for your souls.',
        citation: 'Matthew 11:28-30',
      },
      {
        text: 'God is our refuge and strength, a very present help in trouble.',
        citation: 'Psalm 46:1',
      },
      {
        text: 'For God so loved the world that He gave His only begotten Son, that whoever believes in Him should not perish but have everlasting life.',
        citation: 'John 3:16',
      },
      {
        text: 'And we know that all things work together for good to those who love God, to those who are the called according to His purpose.',
        citation: 'Romans 8:28',
      },
      {
        text: 'For I know the thoughts that I think toward you, says the Lord, thoughts of peace and not of evil, to give you a future and a hope.',
        citation: 'Jeremiah 29:11',
      },
      {
        text: 'Fear not, for I am with you; be not dismayed, for I am your God. I will strengthen you, yes, I will help you, I will uphold you with My righteous right hand.',
        citation: 'Isaiah 41:10',
      },
      {
        text: 'The Lord is my shepherd; I shall not want. He makes me to lie down in green pastures; He leads me beside the still waters. He restores my soul.',
        citation: 'Psalm 23:1-4',
      },
      {
        text: 'I can do all things through Christ who strengthens me.',
        citation: 'Philippians 4:13',
      },
      {
        text: 'My grace is sufficient for you, for My strength is made perfect in weakness.',
        citation: '2 Corinthians 12:9',
      },
      {
        text: 'But God demonstrates His own love toward us, in that while we were still sinners, Christ died for us.',
        citation: 'Romans 5:8',
      },
      { text: 'I will never leave you nor forsake you.', citation: 'Hebrews 13:5' },
      {
        text: 'The Lord is near to those who have a broken heart, and saves such as have a contrite spirit.',
        citation: 'Psalm 34:18',
      },
      {
        text: 'Through the Lord’s mercies we are not consumed, because His compassions fail not. They are new every morning.',
        citation: 'Lamentations 3:22-23',
      },
      {
        text: 'Let not your heart be troubled; you believe in God, believe also in Me. In My Father’s house are many mansions.',
        citation: 'John 14:1-3',
      },
      {
        text: 'O Lord, You have searched me and known me. You know my sitting down and my rising up; You understand my thought afar off.',
        citation: 'Psalm 139:1-12',
      },
      {
        text: 'I am persuaded that neither death nor life... nor any other created thing, shall be able to separate us from the love of God which is in Christ Jesus our Lord.',
        citation: 'Romans 8:38-39',
      },
      {
        text: 'Fear not, for I have redeemed you; I have called you by your name; you are Mine. When you pass through the waters, I will be with you.',
        citation: 'Isaiah 43:1-2',
      },
      {
        text: 'Do not worry about your life, what you will eat or what you will drink... Look at the birds of the air, for they neither sow nor reap; yet your heavenly Father feeds them.',
        citation: 'Matthew 6:25-34',
      },
      {
        text: 'Trust in the Lord with all your heart, and lean not on your own understanding; in all your ways acknowledge Him, and He shall direct your paths.',
        citation: 'Proverbs 3:5-6',
      },
      {
        text: 'In this the love of God was manifested toward us, that God has sent His only begotten Son into the world, that we might live through Him.',
        citation: '1 John 4:9-10',
      },
      {
        text: 'I have come that they may have life, and that they may have it more abundantly.',
        citation: 'John 10:10',
      },
      {
        text: 'For You, Lord, are good, and ready to forgive, and abundant in mercy to all those who call upon You.',
        citation: 'Psalm 86:5',
      },
      {
        text: 'I have been crucified with Christ; it is no longer I who live, but Christ lives in me; and the life which I now live in the flesh I live by faith in the Son of God.',
        citation: 'Galatians 2:20',
      },
      {
        text: 'For by grace you have been saved through faith, and that not of yourselves; it is the gift of God, not of works, lest anyone should boast.',
        citation: 'Ephesians 2:8-9',
      },
      { text: 'Draw near to God and He will draw near to you.', citation: 'James 4:8' },
      {
        text: 'He was wounded for our transgressions, He was bruised for our iniquities; the chastisement for our peace was upon Him, and by His stripes we are healed.',
        citation: 'Isaiah 53:5',
      },
      {
        text: 'The Lord is my light and my salvation; whom shall I fear? The Lord is the strength of my life; of whom shall I be afraid?',
        citation: 'Psalm 27:1',
      },
      {
        text: 'Love suffers long and is kind; love does not envy; love does not parade itself, is not puffed up. Love bears all things, believes all things, hopes all things, endures all things.',
        citation: '1 Corinthians 13:4-7',
      },
      {
        text: 'Be strong and of good courage; do not be afraid, nor be dismayed, for the Lord your God is with you wherever you go.',
        citation: 'Joshua 1:9',
      },
      {
        text: 'Ask, and it will be given to you; seek, and you will find; knock, and it will be opened to you.',
        citation: 'Matthew 7:7-8',
      },
      {
        text: 'In the world you will have tribulation; but be of good cheer, I have overcome the world.',
        citation: 'John 16:33',
      },
      {
        text: 'This is the day the Lord has made; we will rejoice and be glad in it.',
        citation: 'Psalm 118:24',
      },
      {
        text: 'And God will wipe away every tear from their eyes; there shall be no more death, nor sorrow, nor crying. There shall be no more pain, for the former things have passed away.',
        citation: 'Revelation 21:4',
      },
      {
        text: 'That you, being rooted and grounded in love, may be able to comprehend with all the saints what is the width and length and depth and height — to know the love of Christ which passes knowledge.',
        citation: 'Ephesians 3:17-19',
      },
      {
        text: 'He who dwells in the secret place of the Most High shall abide under the shadow of the Almighty. I will say of the Lord, “He is my refuge and my fortress; my God, in Him I will trust.”',
        citation: 'Psalm 91:1-2',
      },
    ],
  },
}

/**
 * Universal-starting set — shown on step 6 when the user picked NOTHING in
 * step 5. Different framing ("Where many start") with a curated 5-card stack
 * of the most-asked-about / most-foundational starting verses.
 */
export const UNIVERSAL_STARTING = {
  label: 'Where many start',
  headerText: 'Where many Christians start.',
  cards: [
    {
      eyebrow: 'For the weary',
      text: 'Come to Me, all you who labor and are heavy laden, and I will give you rest.',
      citation: 'Matthew 11:28',
    },
    {
      eyebrow: 'For God’s love',
      text: 'For God so loved the world that He gave His only begotten Son, that whoever believes in Him should not perish but have everlasting life.',
      citation: 'John 3:16',
    },
    {
      eyebrow: 'For guidance',
      text: 'The Lord is my shepherd; I shall not want.',
      citation: 'Psalm 23:1',
    },
    {
      eyebrow: 'For strength',
      text: 'I can do all things through Christ who strengthens me.',
      citation: 'Philippians 4:13',
    },
    {
      eyebrow: 'For uncertain seasons',
      text: 'For I know the thoughts that I think toward you, says the Lord, thoughts of peace and not of evil, to give you a future and a hope.',
      citation: 'Jeremiah 29:11',
    },
  ],
}

// Helper: number of verses for a struggle key (or 0 if not found).
export function poolSizeFor(key) {
  return STRUGGLE_VERSES[key]?.verses.length ?? 0
}

// Helper: sum pool sizes across a list of struggle keys.
// Dedupes the keys so a duplicate persona-fixture entry doesn't double-count.
export function totalPoolFor(keys) {
  if (!Array.isArray(keys)) return 0
  const unique = [...new Set(keys.filter(Boolean))]
  return unique.reduce((sum, k) => sum + poolSizeFor(k), 0)
}
